
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../../../components/base/Button';
import Card from '../../../components/base/Card';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [fullName, setFullName] = useState('');
  const [userRole, setUserRole] = useState<'teacher' | 'parent' | 'student'>('student');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (isSignUp) {
      // Simulate email verification sending
      setTimeout(() => {
        setLoading(false);
        setEmailSent(true);
      }, 1000);
    } else {
      // Simulate sign in
      setTimeout(() => {
        setLoading(false);
        // Set user with role for testing
        const userData = {
          name: fullName || "Demo User",
          email: email,
          isTeacher: userRole === 'teacher',
          isParent: userRole === 'parent',
          isStudent: userRole === 'student',
          role: userRole,
          id: "demo-user-" + Date.now(),
          emailVerified: true
        };
        localStorage.setItem('user', JSON.stringify(userData));
        navigate('/');
      }, 1000);
    }
  };

  const resendVerification = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-mail-check-line text-white text-3xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Verify Your Email</h2>
            <p className="text-gray-600 mb-6">
              We've sent a verification link to <strong>{email}</strong>. 
              Please check your email and click the link to verify your account.
            </p>
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Didn't receive the email? Check your spam folder or:
              </p>
              <Button
                onClick={resendVerification}
                variant="outline"
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-mail-send-line mr-2"></i>
                    Resend Verification Email
                  </>
                )}
              </Button>
              <button
                onClick={() => {
                  setEmailSent(false);
                  setIsSignUp(false);
                }}
                className="text-[#2d5a35] hover:underline text-sm"
              >
                Back to Sign In
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="https://static.readdy.ai/image/1624bf5855e87197e9f976fe97d0b1c6/f0cc7993a40b5ead0a8769be11e7ac97.png" 
            alt="Reading Adventures Logo" 
            className="w-20 h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-[#2d5a35] mb-2" style={{ fontFamily: '"Pacifico", serif' }}>
            Reading Adventures
          </h1>
          <p className="text-gray-600">
            {inviteCode ? 'Join your teacher\'s class' : 'Welcome to your reading journey'}
          </p>
        </div>

        <Card className="p-8">
          {inviteCode && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3">
                <i className="ri-mail-line text-green-600 text-xl"></i>
                <div>
                  <p className="font-semibold text-green-800">Teacher Invitation</p>
                  <p className="text-sm text-green-600">You've been invited to join a class</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleAuth} className="space-y-6">
            <div className="text-center mb-6">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setIsSignUp(false)}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    !isSignUp ? 'bg-white text-[#2d5a35] shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => setIsSignUp(true)}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    isSignUp ? 'bg-white text-[#2d5a35] shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Sign Up
                </button>
              </div>
            </div>

            {isSignUp && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5a35] focus:border-transparent"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    I am a:
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'teacher', label: 'Teacher', icon: 'ri-user-2-line', color: 'from-blue-500 to-blue-600' },
                      { value: 'parent', label: 'Parent', icon: 'ri-parent-line', color: 'from-purple-500 to-purple-600' },
                      { value: 'student', label: 'Student', icon: 'ri-user-3-line', color: 'from-green-500 to-green-600' }
                    ].map((role) => (
                      <button
                        key={role.value}
                        type="button"
                        onClick={() => setUserRole(role.value as any)}
                        className={`p-4 rounded-xl border-2 transition-all text-center ${
                          userRole === role.value
                            ? 'border-[#2d5a35] bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-10 h-10 bg-gradient-to-br ${role.color} rounded-full flex items-center justify-center mx-auto mb-2`}>
                          <i className={`${role.icon} text-white text-lg`}></i>
                        </div>
                        <span className={`text-sm font-medium ${
                          userRole === role.value ? 'text-[#2d5a35]' : 'text-gray-600'
                        }`}>
                          {role.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5a35] focus:border-transparent"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5a35] focus:border-transparent"
                placeholder="Enter your password"
                required
                minLength={6}
              />
              {isSignUp && (
                <p className="text-sm text-gray-500 mt-1">
                  Password must be at least 6 characters long
                </p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {isSignUp && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-blue-600 text-lg mt-0.5"></i>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Email Verification Required</p>
                    <p>After creating your account, you'll need to verify your email address before you can sign in.</p>
                  </div>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-3 text-lg"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line mr-2 animate-spin"></i>
                  {isSignUp ? 'Creating Account...' : 'Signing In...'}
                </>
              ) : (
                <>
                  <i className={`${isSignUp ? 'ri-user-add-line' : 'ri-login-box-line'} mr-2`}></i>
                  {isSignUp ? 'Create Account' : 'Sign In'}
                </>
              )}
            </Button>
          </form>

          {!inviteCode && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-600">
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                <button
                  onClick={() => setIsSignUp(!isSignUp)}
                  className="ml-1 text-[#2d5a35] font-medium hover:underline"
                >
                  {isSignUp ? 'Sign In' : 'Sign Up'}
                </button>
              </p>
            </div>
          )}
        </Card>

        {!inviteCode && (
          <div className="mt-6 text-center">
            <Card className="p-4">
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                <i className="ri-information-line"></i>
                <span>Teachers can create invite links for their students</span>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
